// Capacitor status-bar helper.
//
// On Capacitor Android, the default is: WebView sits BELOW the status
// bar (the OS shrinks the WebView so status-bar icons render on top of
// the theme's `statusBarColor`, not the app's content). That means
// `env(safe-area-inset-top)` is always 0 and the app looks like it has
// no status bar at all — no time / battery / signal icons visible.
//
// This helper calls `setOverlaysWebView({ overlay: true })` at app
// startup so the WebView extends behind the status bar on Android to
// match iOS's default behaviour. Combined with the CSS
// `env(safe-area-inset-top)` inset and the fixed backdrop in each
// layout, the OS status-bar icons render CLEANLY on top of the
// tenant's background colour with the app content pushed below.
//
// On regular web browsers `window.Capacitor` is undefined and every
// call becomes a no-op — safe to import anywhere.

/** True only inside a Capacitor native shell. */
function inCapacitor() {
  return typeof window !== "undefined" && !!window.Capacitor;
}

/** Configure the native status bar. Call ONCE at app boot.
 *
 * `bgColor` should match the layout's own background — status-bar icons
 * render on top of this colour. `style` = "dark" for dark icons on
 * light backgrounds, "light" for light icons on dark. */
export async function configureStatusBar({ bgColor = "#FAF9F6", style = "dark" } = {}) {
  if (!inCapacitor()) return;
  const StatusBar = window.Capacitor.Plugins?.StatusBar;
  if (!StatusBar) return;   // Plugin not installed on this build — silent no-op.
  try {
    await StatusBar.setOverlaysWebView({ overlay: true });
    // Capacitor's Style enum is named after the ICON colour, not the
    // background it's meant for — `Style.Dark` = light/white icons (for
    // a DARK background), `Style.Light` = dark icons (for a LIGHT
    // background). Our own `style` param instead names what icon colour
    // the caller wants ("dark" icons for a light bg, "light" icons for a
    // dark bg), so it maps to the OPPOSITE Capacitor constant.
    await StatusBar.setStyle({ style: style === "light" ? "DARK" : "LIGHT" });
    // Android also honours a solid `backgroundColor` — but when overlay
    // is true the icons render OVER the WebView bg, so setting this
    // colour is mostly a no-op there. iOS ignores it entirely. Kept
    // for older Android WebViews where overlay isn't fully wired.
    if (bgColor) {
      await StatusBar.setBackgroundColor({ color: bgColor });
    }
  } catch (e) {
    // Silent — worst case the app renders as before (no worse than not
    // installing the plugin). Never crash boot on a status-bar tweak.
    // eslint-disable-next-line no-console
    console.warn("[statusBar] configure failed:", e?.message || e);
  }
}
